<div class="step-form-dialog-container">
  <!-- Modal Header -->
  <div class="modal-header">
    <h2 class="modal-title">{{ data.title }}</h2>
    <button
      mat-icon-button
      class="close-button"
      (click)="onCancel()"
      aria-label="Close"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <form [formGroup]="stepForm" (ngSubmit)="onSubmit()">
    <div mat-dialog-content class="dialog-content">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>
          <mat-icon class="field-label-icon" aria-hidden="true">label</mat-icon>
          Step Name
        </mat-label>
        <input matInput formControlName="name" placeholder="Enter step name" />
        @if (stepForm.get("name")?.hasError("required")) {
          <mat-error> Name is required </mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>
          <mat-icon class="field-label-icon" aria-hidden="true"
            >category</mat-icon
          >
          Step Type
        </mat-label>
        <mat-select
          [formControlName]="stepTypes.length > 0 ? 'steptypeId' : 'steptypeId'"
        >
          <mat-select-trigger>
            @if (getSelectedStepType(); as selectedType) {
              <mat-icon class="select-trigger-icon">{{
                getStepTypeIcon(selectedType)
              }}</mat-icon>
              <span>{{ selectedType.name }}</span>
            }
          </mat-select-trigger>
          @for (type of getAvailableTypes(); track type.steptypeId) {
            <mat-option [value]="getTypeId(type)">
              <mat-icon class="option-icon">{{
                getStepTypeIcon(type)
              }}</mat-icon>
              <span>{{ type.name }}</span>
            </mat-option>
          }
        </mat-select>
        @if (
          stepForm.get("steptypeId")?.hasError("required") ||
          stepForm.get("steptypeId")?.hasError("required")
        ) {
          <mat-error> Step type is required </mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>
          <mat-icon class="field-label-icon" aria-hidden="true"
            >description</mat-icon
          >
          Description
        </mat-label>
        <textarea
          matInput
          formControlName="description"
          placeholder="Enter step description (optional)"
          rows="2"
        >
        </textarea>
      </mat-form-field>

      <!-- AI Generation Section (new steps only, when LLM available) -->
      @if (showAiSection) {
        <div class="ai-generate">
          <div class="ai-generate__toggle">
            <mat-checkbox
              [checked]="aiEnabled"
              (change)="toggleAi()"
              color="primary"
            >
              Generate code with AI
            </mat-checkbox>
            <span class="ai-generate__hint"
              >Use AI to generate starter code from your description</span
            >
          </div>
          @if (aiEnabled) {
            <div class="ai-generate__prompt-area">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>
                  <mat-icon class="field-label-icon" aria-hidden="true"
                    >auto_awesome</mat-icon
                  >
                  Additional Instructions (optional)
                </mat-label>
                <textarea
                  matInput
                  [(ngModel)]="aiInstructions"
                  [ngModelOptions]="{ standalone: true }"
                  placeholder="e.g., Use pandas for data processing, include error handling..."
                  rows="2"
                >
                </textarea>
              </mat-form-field>
              <div class="ai-generate__context">
                <div class="ai-generate__context-title">
                  <mat-icon>info_outline</mat-icon>
                  Context sent to AI
                </div>
                <div class="ai-generate__context-items">
                  @if (data.projectName) {
                    <div><strong>Project:</strong> {{ data.projectName }}</div>
                  }
                  @if (getSelectedStepType(); as selectedType) {
                    <div>
                      <strong>Step type:</strong> {{ selectedType.name }}
                    </div>
                  }
                  @if (priorSteps.length > 0) {
                    <div>
                      <strong>Prior steps:</strong> {{ priorStepNames }}
                    </div>
                  }
                  <div class="ai-generate__context-note">
                    Connections and API reference are included automatically by
                    the server.
                  </div>
                </div>
              </div>
              <button
                mat-stroked-button
                color="primary"
                type="button"
                (click)="generateCode()"
                [disabled]="isGenerating()"
                class="ai-generate__button"
              >
                @if (!isGenerating()) {
                  <mat-icon>auto_awesome</mat-icon>
                }
                @if (isGenerating()) {
                  <mat-spinner diameter="18"></mat-spinner>
                }
                {{ isGenerating() ? "Generating..." : "Generate Code" }}
              </button>
            </div>
          }
          <!-- Generation error -->
          @if (generationError()) {
            <div class="ai-generate__error">
              <mat-icon>error_outline</mat-icon>
              {{ generationError() }}
            </div>
          }
          <!-- Code preview -->
          @if (generatedCode() || isGenerating()) {
            <div class="ai-preview">
              <div class="ai-preview__header">
                <span class="ai-preview__label">
                  <mat-icon>code</mat-icon>
                  Generated Code
                </span>
                <div class="ai-preview__actions">
                  @if (isGenerating()) {
                    <button
                      mat-icon-button
                      (click)="stopGeneration()"
                      matTooltip="Stop generation"
                      aria-label="Stop generation"
                      type="button"
                    >
                      <mat-icon>stop</mat-icon>
                    </button>
                  }
                  @if (!isGenerating() && generatedCode()) {
                    <button
                      mat-icon-button
                      (click)="regenerate()"
                      matTooltip="Regenerate"
                      aria-label="Regenerate code"
                      type="button"
                    >
                      <mat-icon>refresh</mat-icon>
                    </button>
                  }
                </div>
              </div>
              <pre
                class="ai-preview__code"
                #codePreview
              ><code>{{ generatedCode() }}</code></pre>
              @if (generationMeta()) {
                <div class="ai-preview__meta">
                  @if (generationMeta()!.model) {
                    <span>Model: {{ generationMeta()!.model }}</span>
                  }
                  @if (generationMeta()!.usage) {
                    <span>
                      Tokens: {{ generationMeta()!.usage!.inputTokens }} in /
                      {{ generationMeta()!.usage!.outputTokens }} out
                    </span>
                  }
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- Only show content editor when editing existing steps, not when creating new ones -->
      @if (!isCreatingNewStep) {
        <div class="code-editor-container">
          <label class="code-editor-label">
            <mat-icon class="field-label-icon">code</mat-icon>
            Content
          </label>
          <app-code-editor
            [mode]="currentEditorMode()"
            [content]="stepForm.value.content || ''"
            (updateContent)="onContentUpdate($event)"
            (startEdit)="setFormTouched()"
          ></app-code-editor>
        </div>
      }
    </div>

    <div mat-dialog-actions class="dialog-actions">
      <button mat-button type="button" (click)="onCancel()">Cancel</button>
      <button
        mat-raised-button
        color="primary"
        type="submit"
        [disabled]="stepForm.invalid || isGenerating()"
      >
        @if (aiEnabled && generatedCode()) {
          <mat-icon>auto_awesome</mat-icon>
        }
        {{
          isCreatingNewStep
            ? aiEnabled && generatedCode()
              ? "Create with AI"
              : "Create"
            : "Save"
        }}
      </button>
    </div>
  </form>
</div>
